/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../auth";
import { listDishes, updateDish } from "../../../dish/core/_requests";
import { toast } from "react-toastify";
import {
  bulkDeleteDishes,
  bulkEnableDisableDishes,
  ProfileDetails,
} from "../../core/_requests";
import PlanUnsubscribe from "./PlanUnsubscribe";

interface Dish {
  primaryId: string;
  dishImage: string;
  name: string;
  cuisine: any;
  dishType: any;
  category: any;
  type: any;
  tags: string;
  preparationTime: any;
  enableDisableStatus: boolean;
  addons: Addon[];
  dishvariants: DishVariant[];
}

interface Addon {
  id: number;
  name: string;
  quantity: number;
  price: number;
}

interface DishVariant {
  id: number;
  unit: number;
  measurementId: number;
  price: number;
  discount: number;
  finalPrice: number;
}

const FREE_PLAN_LIMIT = 10; // Free plan allows 10 dishes only

const ListDishSubscription = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [listDish, setListDish] = useState<Dish[]>([]);
  const chefId = currentUser?.uuid;
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [dish_count, setDishCount] = useState<any>();
  const [plan, setPlan] = useState<any>();
  const [listPlanDish, setListPlanDish] = useState<any>(null);

  const [showModal, setShowModal] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  const fetchProfileDetails = async () => {
    try {
      const res = await ProfileDetails(chefId);
      setListPlanDish(res?.data);

      const chefPlanAccess =
        res.data?.chef_plan_subscriptions?.[0]?.plan?.chef_plan_accesses?.[0];
      setPlan(res.data?.chef_plan_subscriptions?.[0]?.plan);

      if (chefPlanAccess) {
        setDishCount(chefPlanAccess.dish_count);
      }

      return res.data;
    } catch (error) {
      console.error("Error fetching profile details:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchProfileDetails();
    fetchDishes();
  }, [chefId]);

  const fetchDishes = async () => {
    try {
      const res = await listDishes(chefId, {});
      setListDish(res?.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dishes:", error);
      setLoading(false);
    }
  };

  const handleItemSelect = (primaryId: string) => {
    if (selectedItems.includes(primaryId)) {
      setSelectedItems(selectedItems.filter((itemId) => itemId !== primaryId));
    } else {
      setSelectedItems([...selectedItems, primaryId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === listDish.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(listDish.map((dish) => dish.primaryId));
    }
  };

  const handleEnableDisable = (id: any, status: any) => {
    const enableDisable = !status;
    const data = { enableDisableStatus: enableDisable };

    updateDish(id, data)
      .then((res) => {
        fetchDishes();
        fetchProfileDetails();
        toast.success(res?.message, { autoClose: 5000 });
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error updating dish:", error);
        setLoading(false);
      });
  };

  const handleEnableDisableALL = (action: "enable" | "disable") => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one dish.", { autoClose: 5000 });
      return;
    }

    const enableDisableStatus = action === "enable";

    const data = {
      ids: selectedItems,
      enableDisableStatus: enableDisableStatus,
    };

    bulkEnableDisableDishes(data)
      .then((res) => {
        if (res.status === true) {
          toast.success(
            res?.message || "Dish status updated successfully!",
            { autoClose: 5000 }
          );
          fetchProfileDetails();
          fetchDishes();
        } else {
          toast.error(res?.message, { autoClose: 5000 });
        }
      })
      .catch((error) => {
        console.error("Error updating dish status:", error);
        toast.error("Failed to update dish status.", { autoClose: 5000 });
      });
  };

  const deleteDishAll = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one dish to delete.", {
        autoClose: 5000,
      });
      return;
    }
    setLoading(true);
    try {
      const res = await bulkDeleteDishes({ dishIds: selectedItems });
      if (res.status === true) {
        toast.success(
          res.message || "Selected dishes deleted successfully!",
          { autoClose: 5000 }
        );
        setSelectedItems([]);
        fetchDishes();
        const updatedData = await fetchProfileDetails();

        const totalDishCount = updatedData?.totalDishCount || 0;
        if (totalDishCount > FREE_PLAN_LIMIT) {
          setShowMessage(true);
        } else {
          setShowMessage(false);
          setShowModal(true);
        }
      } else {
        toast.error(res?.message, { autoClose: 5000 });
      }
    } catch (error) {
      console.error("Error deleting dishes:", error);
      toast.error("Failed to delete selected dishes.", { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const deleteDish = (primaryId: string) => {
    navigate("/dish/delete/" + primaryId);
  };

  const cancelDishSubscription = () => {
    navigate("/settings");
  };

  const showFinalConfirmationModal = () => {
  const totalDishCount = listPlanDish?.totalDishCount || 0;

  // Find any bulk dishes from existing list
  const bulkDishes = listDish.filter((item) => item.dishType === 2);

  if (bulkDishes.length > 0) {
    toast.error(
      `You have ${bulkDishes.length} bulk dish${
        bulkDishes.length > 1 ? "es" : ""
      }. Please delete all bulk dishes before unsubscribing.`,
      { autoClose: 5000 }
    );
    return;
  }

  if (totalDishCount > FREE_PLAN_LIMIT) {
    const extra = totalDishCount - FREE_PLAN_LIMIT;
    setShowMessage(true);
    setShowModal(false);
    toast.error(
      `Delete ${extra} dish${extra > 1 ? "es" : ""} before unsubscribing.`,
      { autoClose: 5000 }
    );
  } else {
    setShowMessage(false);
    setShowModal(true);
  }
};


  return (
    <section className="container-fluid">
      <div className="row">
        <section className="content-section">
          <section className="row main-content">
            <div className="title mb-3 mt-3">
              <h3 className="mb-0 d-flex justify-content-between align-items-center">
                <div>
                  Dishes ({listDish.length}) -
                  <span className="mb-0 text-success">
                    {" "}
                    {plan?.name} ({dish_count})
                  </span>
                </div>
                {selectedItems.length > 0 && (
                  <div className="action-buttons">
                    <span className="selected-count me-3">
                      Selected: {selectedItems.length}
                    </span>
                    {/* <button
                      onClick={() => handleEnableDisableALL("enable")}
                      className="btn btn-outline-primary rounded-5 me-3"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={() => handleEnableDisableALL("disable")}
                      className="btn btn-outline-warning rounded-5 me-3"
                    >
                      Disable All
                    </button> */}
                    <button
                      onClick={deleteDishAll}
                      className="btn btn-outline-danger rounded-5 me-2"
                    >
                      Delete All
                    </button>
                  </div>
                )}
              </h3>
            </div>

            <div className="table-wrap mt-4 mb-3">
              <table className="table custom-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          selectedItems?.length === listDish?.length &&
                          selectedItems?.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>S.No</th>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Cuisine-Category</th>
                    <th></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listDish.map((item, i) => (
                    <tr key={item.primaryId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item?.primaryId)}
                          onChange={() => handleItemSelect(item?.primaryId)}
                        />
                      </td>
                      <td>{i + 1}</td>
                      <td
                        className={
                          item?.dishType === 2 ? "text-success fs-bold" : ""
                        }
                      >
                        {item?.dishType === 1
                          ? "Regular"
                          : item?.dishType === 2
                          ? "Bulk"
                          : ""}
                      </td>
                      <td>
                        <div className="dish">
                          <div className="thumbnail">
                            <img src={item?.dishImage} alt="Dish Name" />
                          </div>
                          <p className="ms-2 mb-0">{item?.name}</p>
                        </div>
                      </td>
                      <td>
                        {item.cuisine?.name || item.cuisine || "N/A"} -{" "}
                        {item?.category?.name}
                      </td>
                      <td>
                        {/* <div className="form-check form-switch">
                          <input
                            className="form-check-input cursorPointer"
                            type="checkbox"
                            role="switch"
                            checked={item?.enableDisableStatus}
                            onClick={() =>
                              handleEnableDisable(
                                item?.primaryId,
                                item?.enableDisableStatus
                              )
                            }
                          />
                          <label className="yes">Yes</label>
                          <label className="no">No</label>
                        </div> */}
                      </td>
                      <td>
                        <div className="action-item">
                          <a
                            className="danger cursorPointer"
                            onClick={() => deleteDish(item?.primaryId)}
                          >
                            <span className="material-symbols-outlined">
                              delete
                            </span>
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="container mt-3 ">
              <div className="row">
                <div className="col-12">
                  {showMessage && (
                    <p className="text-danger mt-2 mb-3">
                      <strong> Please Note: </strong>
                      You can’t unsubscribe to the free plan because you
                      currently have {listPlanDish?.totalDishCount} dishes, but
                      only {FREE_PLAN_LIMIT} are allowed. <br />
                      Please delete{" "}
                      {listPlanDish?.totalDishCount - FREE_PLAN_LIMIT} extra
                      dish
                      {listPlanDish?.totalDishCount - FREE_PLAN_LIMIT > 1
                        ? "es"
                        : ""}{" "}
                      to proceed.
                    </p>
                  )}

                  <div className="d-flex justify-content-end mb-5 ">
                    <button
                      onClick={cancelDishSubscription}
                      className="btn btn-outline-danger rounded-5 me-2"
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-outline-success rounded-5 me-2"
                      onClick={showFinalConfirmationModal}
                      disabled={
                        (listPlanDish?.totalDishCount || 0) > FREE_PLAN_LIMIT
                      }
                    >
                      Proceed Unsubscription
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {showModal && <PlanUnsubscribe showFinalModal={showModal} />}
          </section>
        </section>
      </div>
    </section>
  );
};

export default ListDishSubscription;
